/**
 * Pure form helpers for the purchase order create/edit screen.
 * Contract: see repo root `purchase-orders-api.md`.
 */

import { format } from "date-fns";

import { normalizeDropdownOption } from "../shared/locationDropdowns";
import { generateUniqueNumber } from "../shared/utility";
import type {
  AssemblyItemDropdownRaw,
  DropdownOption,
  PurchaseOrderDetail,
  PurchaseOrderDetailResponse,
  PurchaseOrderFormLineItem,
  PurchaseOrderFormState,
  PurchaseOrderLineSummary,
  PurchaseOrderReceivingInvoice,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
  PurchaseOrderValidationResult,
  TaxRateSnapshot,
} from "./purchaseOrderTypes";

/** Line row from either receiving summary or saved PO items on detail load. */
export interface PurchaseOrderDetailLineSource {
  purchase_order_item_id?: number;
  id?: number;
  assembly_item_id?: number;
  product_name?: string;
  supplier_plu?: string | null;
  description?: string | null;
  ordered_amount?: number | string;
  received_amount?: number;
  remaining_amount?: number;
  fulfillment_percent?: number;
  unit_of_measure?: DropdownOption | null;
  unit_cost?: string | number | null;
  lot_number?: string | null;
  notes?: string | null;
  subtotal?: string | number | null;
  tax_amount?: string | number | null;
  line_total?: string | number | null;
  taxes?: TaxRateSnapshot[];
  assembly_item?: {
    id: number;
    product_name?: string;
  };
}

export interface LineItemTotalsContext {
  supplierTaxes?: TaxRateSnapshot[];
  itemTaxesByAssemblyId?: Record<number, TaxRateSnapshot[]>;
}

export interface LinePricing {
  subtotal: number | null;
  tax_amount: number | null;
  line_total: number | null;
  taxes: TaxRateSnapshot[];
}

export interface LoadedPurchaseOrderForm {
  purchaseOrderId: number | null;
  poStatus: PurchaseOrderStatus;
  poNumber: string | null;
  purchaseOrder: PurchaseOrderFormState;
  lineItems: PurchaseOrderFormLineItem[];
  summary: PurchaseOrderSummary | null;
  receivingInvoices: PurchaseOrderReceivingInvoice[];
}

export function unitOfMeasureFromAssemblyRaw(
  raw: AssemblyItemDropdownRaw | null | undefined,
): DropdownOption | null {
  const id =
    typeof raw?.unit_of_measure === "object"
      ? raw.unit_of_measure?.value
      : raw?.unit_of_measure;
  const label =
    raw?.unit_of_measure_text ??
    (typeof raw?.unit_of_measure === "object"
      ? raw.unit_of_measure?.label
      : "") ??
    "";
  if (id != null && id !== "") return { value: id, label };
  return null;
}

export function unitCostFromAssemblyRaw(
  raw: AssemblyItemDropdownRaw | null | undefined,
): string {
  const cost =
    raw?.cost ??
    (raw as { unit_cost?: string | number | null } | null | undefined)
      ?.unit_cost;
  if (cost == null || cost === "") return "";
  const n = Number(cost);
  if (!Number.isFinite(n) || n < 0) return "";
  return typeof cost === "string" ? cost : String(n);
}

function parsePricingNumber(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasSavedLinePricing(row: PurchaseOrderFormLineItem): boolean {
  return (
    row.subtotal != null ||
    row.tax_amount != null ||
    row.line_total != null ||
    (row.taxes != null && row.taxes.length > 0)
  );
}

function savedLinePricing(row: PurchaseOrderFormLineItem): LinePricing {
  return {
    subtotal: row.subtotal ?? null,
    tax_amount: row.tax_amount ?? null,
    line_total: row.line_total ?? null,
    taxes: row.taxes ?? [],
  };
}

function hasItemTaxesLoaded(
  assemblyItemId: number | null | undefined,
  itemTaxesByAssemblyId?: Record<number, TaxRateSnapshot[]>,
): boolean {
  if (assemblyItemId == null) return true;
  return (
    itemTaxesByAssemblyId != null &&
    Object.prototype.hasOwnProperty.call(itemTaxesByAssemblyId, assemblyItemId)
  );
}

export function createEmptyLineItem(
  tempId: number | string,
): PurchaseOrderFormLineItem {
  return {
    temp_id: tempId,
    assembly_item_id: null,
    item_name: "",
    supplier_plu: "",
    description: "",
    ordered_quantity: "",
    ordered_cartons: "",
    unit_of_measure: null,
    unit_cost: "",
    expected_lot_number: "",
    notes: "",
  };
}

export function createInitialPurchaseOrderFormState(
  location: DropdownOption | null = null,
): PurchaseOrderFormState {
  return {
    supplier_id: null,
    order_date: format(new Date(), "yyyy-MM-dd"),
    expected_delivery_date: "",
    location,
    notes: "",
  };
}

export function mapDetailItemToFormRow(
  item: PurchaseOrderDetailLineSource,
): PurchaseOrderFormLineItem {
  const lineId = item.purchase_order_item_id ?? item.id;
  const received = Number(item.received_amount ?? 0);
  const tempId = lineId ?? generateUniqueNumber();

  return {
    id: lineId ?? null,
    temp_id: tempId,
    assembly_item_id: item.assembly_item_id ?? null,
    item_name: item.product_name ?? item.assembly_item?.product_name ?? "",
    supplier_plu: item.supplier_plu ?? "",
    description:
      item.description ??
      item.product_name ??
      item.assembly_item?.product_name ??
      "",
    ordered_quantity:
      item.ordered_amount != null ? String(item.ordered_amount) : "",
    ordered_cartons: "",
    unit_of_measure: item.unit_of_measure ?? null,
    unit_cost: item.unit_cost != null ? String(item.unit_cost) : "",
    expected_lot_number: item.lot_number ?? "",
    notes: item.notes ?? "",
    received_amount: received,
    remaining_amount:
      item.remaining_amount != null ? Number(item.remaining_amount) : undefined,
    fulfillment_percent:
      item.fulfillment_percent != null
        ? Number(item.fulfillment_percent)
        : undefined,
    line_locked: received > 0,
    subtotal: parsePricingNumber(item.subtotal),
    tax_amount: parsePricingNumber(item.tax_amount),
    line_total: parsePricingNumber(item.line_total),
    taxes: item.taxes ?? [],
  };
}

export function mapDetailResponseToForm(
  data: PurchaseOrderDetailResponse,
): LoadedPurchaseOrderForm {
  const po: Partial<PurchaseOrderDetail> = data.purchase_order ?? {};
  const items: PurchaseOrderDetailLineSource[] = Array.isArray(data.items)
    ? (data.items as PurchaseOrderDetailLineSource[])
    : Array.isArray(po.items)
      ? (po.items as PurchaseOrderDetailLineSource[])
      : [];

  const supplierId = po.supplier_id ?? po.supplier?.id ?? null;

  return {
    purchaseOrderId: po.id ?? null,
    poStatus: (po.status ?? "draft") as PurchaseOrderStatus,
    poNumber: po.po_number ?? null,
    summary: data.summary ?? null,
    receivingInvoices: Array.isArray(data.receiving_invoices)
      ? data.receiving_invoices
      : [],
    purchaseOrder: {
      supplier_id: supplierId,
      supplier_label: po.supplier?.name ?? "",
      order_date: po.order_date
        ? String(po.order_date).slice(0, 10)
        : format(new Date(), "yyyy-MM-dd"),
      expected_delivery_date: po.expected_delivery_date
        ? String(po.expected_delivery_date).slice(0, 10)
        : "",
      location: normalizeDropdownOption(
        po.location ??
          (po as { ship_to_location?: DropdownOption }).ship_to_location,
      ),
      notes: po.notes ?? "",
    },
    lineItems: items.map(mapDetailItemToFormRow),
  };
}

export function lineItemSubtotal(
  item: Pick<PurchaseOrderFormLineItem, "ordered_quantity" | "unit_cost">,
): number | null {
  const qty = Number(item.ordered_quantity);
  const cost = Number(item.unit_cost);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(cost) || cost < 0) {
    return null;
  }
  return qty * cost;
}

export function parseTaxRatePercent(tax: TaxRateSnapshot): number {
  if (tax.rate != null && tax.rate !== "") {
    const fromRate = Number(tax.rate);
    if (Number.isFinite(fromRate)) return fromRate;
  }
  const match = tax.label.match(/\(([\d.]+)%\)/);
  if (match) {
    const fromLabel = Number(match[1]);
    if (Number.isFinite(fromLabel)) return fromLabel;
  }
  return 0;
}

export function intersectTaxRates(
  supplierTaxes: TaxRateSnapshot[] = [],
  itemTaxes: TaxRateSnapshot[] = [],
): TaxRateSnapshot[] {
  const itemIds = new Set(itemTaxes.map((t) => Number(t.value)));
  return supplierTaxes.filter((t) => itemIds.has(Number(t.value)));
}

export function sumTaxRatePercents(taxes: TaxRateSnapshot[]): number {
  return taxes.reduce((sum, tax) => sum + parseTaxRatePercent(tax), 0);
}

export function computeLinePricing(
  qty: number,
  unitCost: number,
  effectiveTaxes: TaxRateSnapshot[],
): LinePricing {
  if (
    !Number.isFinite(qty) ||
    qty <= 0 ||
    !Number.isFinite(unitCost) ||
    unitCost < 0
  ) {
    return {
      subtotal: null,
      tax_amount: null,
      line_total: null,
      taxes: effectiveTaxes,
    };
  }

  const subtotal = qty * unitCost;
  const rateSum = sumTaxRatePercents(effectiveTaxes);
  const tax_amount = subtotal * (rateSum / 100);
  const line_total = subtotal + tax_amount;

  return {
    subtotal,
    tax_amount,
    line_total,
    taxes: effectiveTaxes,
  };
}

export function pricingForLineItem(
  row: PurchaseOrderFormLineItem,
  context: LineItemTotalsContext = {},
): LinePricing {
  const itemTaxesByAssemblyId = context.itemTaxesByAssemblyId ?? {};

  if (
    !hasItemTaxesLoaded(row.assembly_item_id, itemTaxesByAssemblyId) &&
    hasSavedLinePricing(row)
  ) {
    return savedLinePricing(row);
  }

  const supplierTaxes = context.supplierTaxes ?? [];
  const itemTaxes =
    row.assembly_item_id != null
      ? (itemTaxesByAssemblyId[row.assembly_item_id] ?? [])
      : [];
  const effectiveTaxes = intersectTaxRates(supplierTaxes, itemTaxes);
  const qty = Number(row.ordered_quantity);
  const unitCost = Number(row.unit_cost);
  return computeLinePricing(qty, unitCost, effectiveTaxes);
}

export function attachLineItemTotals(
  lineItems: PurchaseOrderFormLineItem[],
  context: LineItemTotalsContext = {},
): PurchaseOrderFormLineItem[] {
  return lineItems.map((row) => {
    const pricing = pricingForLineItem(row, context);
    return {
      ...row,
      subtotal: pricing.subtotal,
      tax_amount: pricing.tax_amount,
      line_total: pricing.line_total,
      taxes: pricing.taxes,
    };
  });
}

export function validatePurchaseOrder(
  purchaseOrder: PurchaseOrderFormState,
  lineItems: PurchaseOrderFormLineItem[],
): PurchaseOrderValidationResult {
  if (!purchaseOrder?.supplier_id) {
    return { valid: false, message: "Please select a supplier." };
  }
  if (!purchaseOrder?.expected_delivery_date) {
    return {
      valid: false,
      message: "Please select an expected delivery date.",
    };
  }
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    return { valid: false, message: "Please add at least one item." };
  }
  for (let i = 0; i < lineItems.length; i += 1) {
    const row = lineItems[i];
    if (!row.assembly_item_id) {
      return {
        valid: false,
        message: `Line ${i + 1}: please select an item.`,
      };
    }
    const qty = Number(row.ordered_quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      return {
        valid: false,
        message: "Each item must have an ordered quantity greater than 0.",
      };
    }
    if (row.ordered_cartons !== "" && row.ordered_cartons != null) {
      const cartons = Number(row.ordered_cartons);
      if (!Number.isFinite(cartons) || cartons < 0) {
        return {
          valid: false,
          message: `Line ${i + 1}: cartons must be zero or greater.`,
        };
      }
    }
    if (row.unit_cost !== "" && row.unit_cost != null) {
      const cost = Number(row.unit_cost);
      if (!Number.isFinite(cost) || cost < 0) {
        return {
          valid: false,
          message: `Line ${i + 1}: unit cost must be zero or greater.`,
        };
      }
    }
  }
  return { valid: true };
}

export function summarizePurchaseOrderLines(
  lineItems: PurchaseOrderFormLineItem[] | null | undefined,
): PurchaseOrderLineSummary {
  const rows = lineItems || [];
  let totalQty = 0;
  let totalCartons = 0;
  let subtotalSum = 0;
  let taxSum = 0;
  let grandSum = 0;
  let hasPricing = false;

  rows.forEach((row) => {
    const qty = Number(row.ordered_quantity);
    if (Number.isFinite(qty)) totalQty += qty;
    const cartons = Number(row.ordered_cartons);
    if (Number.isFinite(cartons)) totalCartons += cartons;

    if (row.subtotal != null && Number.isFinite(row.subtotal)) {
      subtotalSum += row.subtotal;
      taxSum += row.tax_amount ?? 0;
      grandSum += row.line_total ?? row.subtotal + (row.tax_amount ?? 0);
      hasPricing = true;
    }
  });

  return {
    lineCount: rows.length,
    totalQty,
    totalCartons,
    subtotal: hasPricing ? subtotalSum : null,
    taxTotal: hasPricing ? taxSum : null,
    grandTotal: hasPricing ? grandSum : null,
  };
}
